/**
 * « T'attend » en direct (01/10/2026). The server alone says what waits on Gilles
 * (`flux_waiting_state`: the « t'attend » number, the keys of the lines that wait, a fingerprint
 * of them) and pushes a message on the private Realtime topic `flux:waiting` whenever it may have
 * moved. This module decides, purely, what a fresh read changes — the nav badge, the list's keys
 * and the liveness mark always, the cockpit's readers only when what waits really moved.
 */

/** Realtime topic and event sent by the `flux_waiting_touch` triggers (migration 20261001123812). */
export const WAITING_TOPIC = 'flux:waiting';
export const WAITING_EVENT = 'changed';
/**
 * A read every minute while the channel is live: the passing of time moves lines without any
 * write (a report that ends, a block that starts) and no trigger sees it. Every 15 s while the
 * channel is down, so a dropped socket never leaves the page behind for long.
 */
export const WAITING_RECONCILE_MS = 60_000;
export const WAITING_FALLBACK_POLL_MS = 15_000;
/** Without the channel, past this without a successful read the page no longer claims to follow. */
export const WAITING_STALE_MS = 45_000;

export interface WaitingState {
  count: number;
  keys: string[];
  /** Line key → its exchange group (`exchange:<id>`), for the lines that come from an exchange. */
  groups: Record<string, string>;
  fingerprint: string;
}

export interface WaitingSignal {
  /** The « t'attend » number; null until the first read. */
  count: number | null;
  /** The keys of the lines that wait, reported ones included; null until the first read. */
  keys: ReadonlySet<string> | null;
  /** The exchange group of each grouped line (one exchange = one element); null until the first read. */
  groups: ReadonlyMap<string, string> | null;
  fingerprint: string | null;
  /** Last successful read (epoch ms). */
  checkedAt: number | null;
  /** The server can reach this page right now (Realtime channel joined). */
  live: boolean;
}

export const INITIAL_SIGNAL: WaitingSignal = {
  count: null,
  keys: null,
  groups: null,
  fingerprint: null,
  checkedAt: null,
  live: false,
};

/**
 * Why a read happened. `server`: the server's message, the timer or the page coming back — a
 * change then came from elsewhere (Otto, the other admin) and the cockpit's readers must reload.
 * `gesture`: a gesture on this page already made them reload — telling them again would reload
 * them twice.
 */
export type ReadCause = 'server' | 'gesture';

const sameKeys = (a: ReadonlySet<string> | null, b: readonly string[]) => a !== null && a.size === b.length && b.every((k) => a.has(k));
const sameGroups = (a: ReadonlyMap<string, string> | null, b: Record<string, string>) => {
  if (a === null) return false;
  const entries = Object.entries(b);
  return a.size === entries.length && entries.every(([key, group]) => a.get(key) === group);
};

/** The signal after a read, and whether the cockpit's readers must reload. */
export function applyRead(
  previous: WaitingSignal,
  read: WaitingState,
  cause: ReadCause,
  now: number,
): { signal: WaitingSignal; changed: boolean } {
  const changed = cause === 'server' && previous.fingerprint !== null && previous.fingerprint !== read.fingerprint;
  return {
    signal: {
      ...previous,
      count: read.count,
      // Same lines, same set: the list is not rebuilt for nothing.
      keys: sameKeys(previous.keys, read.keys) ? previous.keys : new Set(read.keys),
      groups: sameGroups(previous.groups, read.groups) ? previous.groups : new Map(Object.entries(read.groups)),
      fingerprint: read.fingerprint,
      checkedAt: now,
    },
    changed,
  };
}

/** Narrows the RPC's jsonb: anything else is a failed read, never « nothing waits ». */
export function parseWaitingState(data: unknown): WaitingState | null {
  if (typeof data !== 'object' || data === null) return null;
  const { count, keys, groups, fingerprint } = data as Record<string, unknown>;
  if (typeof count !== 'number' || typeof fingerprint !== 'string' || !Array.isArray(keys)) return null;
  if (!keys.every((k): k is string => typeof k === 'string')) return null;
  // A server before the grouping (lot 6) sends no `groups`: every line then stands alone.
  if (groups !== undefined && (typeof groups !== 'object' || groups === null || Array.isArray(groups))) return null;
  const pairs = Object.entries((groups ?? {}) as Record<string, unknown>);
  if (!pairs.every((pair): pair is [string, string] => typeof pair[1] === 'string')) return null;
  return { count, keys, groups: Object.fromEntries(pairs), fingerprint };
}

export type Liveness =
  | { kind: 'pending' }
  | { kind: 'live' }
  | { kind: 'polling'; ageMs: number }
  | { kind: 'stale'; ageMs: number };

/** What the cockpit says about its own freshness. */
export function livenessOf(signal: Pick<WaitingSignal, 'checkedAt' | 'live'>, now: number): Liveness {
  if (signal.checkedAt === null) return { kind: 'pending' };
  if (signal.live) return { kind: 'live' };
  const ageMs = Math.max(0, now - signal.checkedAt);
  return ageMs > WAITING_STALE_MS ? { kind: 'stale', ageMs } : { kind: 'polling', ageMs };
}

/** « à l'instant », « il y a 12 s », « il y a 3 min ». */
export function ageText(ageMs: number): string {
  const s = Math.floor(ageMs / 1000);
  if (s < 5) return "à l'instant";
  if (s < 60) return `il y a ${s} s`;
  return `il y a ${Math.floor(s / 60)} min`;
}
